import { CodeEvidenceCitation } from './evidence-citation.model';

/**
 * Turns a code-subsection reference named in report prose into a validated citation (Issue #1773).
 *
 * The compliance report's own header already links the section under review, and the page citation
 * points at the page the reader is already looking at. What a reader actually wants to follow are
 * the *other* provisions the report reasons about — "this assumes a Class I system is required, as
 * determined by other code sections (e.g., IBC 905.3.1 for building height)". This module is how
 * that sentence acquires a link on `905.3.1`.
 *
 * ## Why prose supplies only a number
 *
 * The backend writes the marker, and the backend has already checked the number against the book's
 * own table of contents. But nothing reaching this module can prove that. A compliance statement is
 * one string, and by the time it arrives there is no way to distinguish the part the backend wrote
 * from the part the model wrote — so this module treats the whole statement as model-authored, which
 * is the only safe reading.
 *
 * That is why a marker carries a section number and nothing else. Everything that reaches the URL —
 * the host, the document slug, the chapter path, the anchor prefix — comes from the report's own
 * catalog identity, which arrived as structured data rather than prose. The number is checked for
 * shape and then checked for belonging to the chapter the report is about. So the worst a forged or
 * hallucinated marker can achieve is to name a number in the wrong chapter, which fails that check
 * and renders as plain text.
 *
 * This is the same invariant the F1 security fix established for the report body: model prose never
 * reaches a trusted sink. Moving an identifier into prose does not weaken it, because the identifier
 * is validated rather than trusted.
 *
 * ## The report's own section is linked too
 *
 * An earlier version refused a marker naming the section the report is about, on the grounds that
 * the header already links it. That reasoning came from the sources list, which sat at the foot of
 * the report, away from the sentence that needed it. An inline link is read in context: a reader
 * partway through a paragraph about 905.4 can follow it without scrolling back to the header. The
 * same-chapter check stays, because it is what keeps a fragment from being hung off the wrong
 * chapter path.
 *
 * ## The known limitation
 *
 * A reference to a section in a *different* chapter cannot be linked here, because the report
 * carries the slug of its own chapter only, and a fragment hung off the wrong chapter path is a
 * broken link that looks like a working one. Such a reference stays plain text. That is a missing
 * link rather than a wrong one, which is the trade this feature makes everywhere: a reader who
 * cannot click through is mildly inconvenienced, while a reader sent to the wrong requirement has no
 * signal that anything went wrong.
 */

/**
 * Markers the backend writes for a resolved code reference, e.g. `[^sec:905.3.1]`.
 *
 * Distinct from the evidence-chain marker form `[^e1]` by construction: the ID charset for those is
 * `[A-Za-z0-9_-]`, which admits neither the colon nor the dots here, so the two patterns cannot
 * match the same text. The footnote-ish shape is deliberate — `marked` has no footnote support
 * configured, so it passes the marker through as literal text rather than restructuring the
 * document.
 */
export const INLINE_SECTION_MARKER_PATTERN = /\[\^sec:([A-Za-z]?\d{3,5}(?:\.\d+)*)\]/g;

/**
 * An unresolved generation placeholder, matched only so it can be reduced to its number.
 *
 * The backend resolves these before it stores a statement, so one arriving here means a report was
 * written by a build that did not, or a model wrote something the resolver did not recognise. Either
 * way a reader should see `905.3.1`, never `icc-section://2217/905.3.1`.
 */
export const UNRESOLVED_SECTION_PLACEHOLDER_PATTERN =
  /icc-section:\/\/[A-Za-z0-9.]{1,32}\/([A-Za-z]?\d{3,5}(?:\.\d+)*)/gi;

/** A section number on its own, for validating one candidate rather than scanning text. */
const SECTION_NUMBER_PATTERN = /^[A-Za-z]?\d{3,5}(?:\.\d+)*$/;

/** The chapter ordinal inside a fully qualified section ID, e.g. `_Ch09_`. */
const CHAPTER_ORDINAL_PATTERN = /_Ch(\d+)(?:_|$)/;

/** The trailing section component of a fully qualified section ID, e.g. `_Sec905.4`. */
const SECTION_COMPONENT_PATTERN = /_Sec[0-9A-Za-z][0-9A-Za-z.]*$/;

/**
 * The catalog identity of the report a marker was found in.
 *
 * Assembled by the report tab from the structured payload — the code book and the section hierarchy
 * — never from the statement text.
 */
export interface ReportCodeIdentity {
  bookId: string;
  documentSlug: string;
  chapterSlug: string;
  chapterTitle: string;
  /** Fully qualified section ID of the section under review, e.g. `IBC2021P2_Ch09_Sec905.4`. */
  sectionId: string;
}

/**
 * Builds the citation for one inline section reference, or `null` when it cannot be trusted.
 *
 * Returning `null` rather than a partially-formed citation is the same discipline the mapper and the
 * link service already follow: a caller can only either render a checked link or render text, and
 * there is no third state in which something half-validated reaches a template.
 *
 * @param sectionNumber the number a marker carried, e.g. `905.3.1`
 * @param report the catalog identity of the report the marker appeared in
 * @param sectionTitle the provision's title from the loaded table of contents, when it has one
 */
export function buildInlineCodeCitation(
  sectionNumber: string,
  report: ReportCodeIdentity | null | undefined,
  sectionTitle?: string
): CodeEvidenceCitation | null {
  if (!report) return null;

  const number = sectionNumber.trim();
  if (!SECTION_NUMBER_PATTERN.test(number)) return null;

  // Without a document slug there is no book path, and without the report's own section ID there is
  // nothing to derive an anchor from. Both arrive as structured data or not at all.
  if (!report.documentSlug || !report.sectionId) return null;
  if (!SECTION_COMPONENT_PATTERN.test(report.sectionId)) return null;

  const reportChapter = chapterOrdinalOf(report.sectionId);
  if (reportChapter === null) return null;
  if (chapterOfSectionNumber(number) !== reportChapter) return null;

  // Substituting the section component of the report's own ID preserves everything else about it,
  // including subchapter markers such as `_SubCh02_` that some books carry within a chapter.
  // Composing a fresh ID from parts would silently drop those and produce an anchor that is not on
  // the page.
  const sectionId = report.sectionId.replace(SECTION_COMPONENT_PATTERN, `_Sec${number}`);

  return {
    kind: 'code',
    id: `inline-sec-${number}`,
    claimId: '',
    // Prose addresses this by number, not by an evidence-chain ID, so it is not referenceable in the
    // sense the marker resolver means. It also never appears in the sources list.
    referenceable: false,
    confidence: 'unknown',
    excerpt: '',
    provenance: {
      // The target was derived from catalog identity and a table-of-contents check, with no model
      // judgment about where the reference points.
      method: 'deterministic',
      generatedByModel: '',
      pageArtifactRevision: '',
      regionHierarchyRevision: '',
    },
    code: {
      bookId: report.bookId,
      documentSlug: report.documentSlug,
      chapterSlug: report.chapterSlug,
      chapterTitle: report.chapterTitle,
      sectionId,
      // The number as the prose wrote it, so the visible link text is the wording the reader is
      // already looking at rather than a relabelled version of it.
      visibleLabel: number,
      // Absent rather than empty when the number is not in the loaded contents, so the tooltip can
      // tell "no title known" apart from "title is blank".
      ...(sectionTitle ? { sectionTitle } : {}),
      proseInline: true,
    },
  };
}

/** The chapter ordinal a fully qualified section ID belongs to, or `null` when it has none. */
function chapterOrdinalOf(sectionId: string): number | null {
  const match = CHAPTER_ORDINAL_PATTERN.exec(sectionId);
  if (!match) return null;
  const ordinal = Number(match[1]);
  return Number.isInteger(ordinal) && ordinal > 0 ? ordinal : null;
}

/**
 * The chapter a section number belongs to.
 *
 * Integer division by one hundred, with anything below one hundred clamped to chapter 1 — the same
 * rule the backend's URL builder applies, so a number the backend accepted for a chapter resolves to
 * that same chapter here. A letter prefix (`R302.1`) is stripped for the arithmetic and is otherwise
 * irrelevant to which chapter the section is in.
 */
function chapterOfSectionNumber(sectionNumber: string): number {
  const numeric = /^[A-Za-z]/.test(sectionNumber) ? sectionNumber.slice(1) : sectionNumber;
  const wholePart = Number(numeric.split('.')[0]);
  if (!Number.isInteger(wholePart)) return -1;
  const chapter = Math.floor(wholePart / 100);
  return chapter === 0 ? 1 : chapter;
}
